import { isEmpty } from 'lodash';
import i18next from 'i18next';
import React from 'react';

import { DeepsightLabelSpot } from '@commons/DeepsightComponents';
import {
  Dropdown,
  INPUT_WIDTH,
  ToggleSwitch,
  Tooltip,
} from '@commons/utils/styledLibraryComponents';
import {
  formatTextNumberInputValue,
  sortCategoryAndSubCategoryDropdown,
} from '@commons/utils/format';

import { Input } from '@lib/inpulse/Input';

import theme from '@theme';

import { getTooltipTexts } from '@admin/products/ingredients/constants/tooltipText';
import { isSameUnit } from '@admin/products/ingredients/utils/assertSupplierProductAreSameUnit';
import { SMALL_UNITS_DROPDOWN_ITEMS } from '@admin/utils/DropdownItems';

import { IngredientAllergenDropdown, InlineContainer, Text, ToggleContainer } from './inputs.style';

export class AllergensInput extends React.PureComponent {
  render() {
    const {
      ingredientAllergens,
      availableAllergens,
      onChange,
      isReadOnly,
      supplierProductAllergens,
    } = this.props;

    const allergens =
      supplierProductAllergens?.length > 0 ? supplierProductAllergens : ingredientAllergens;

    return (
      <IngredientAllergenDropdown>
        <Dropdown
          isDisabled={isReadOnly || !isEmpty(supplierProductAllergens)}
          isLabelTextSmall={true}
          isUniqueSelection={false}
          items={availableAllergens}
          label={
            <>
              {i18next.t('ADMIN.INGREDIENTS.LABEL_ALLERGENS')} :
              {allergens && allergens.length > 0 && (
                <DeepsightLabelSpot
                  isTooltip={true}
                  number={allergens.length}
                  tooltipText={`${i18next.t('ADMIN.INGREDIENTS.LABEL_ALLERGENS')} : ${allergens
                    .map(({ translationKey }) => i18next.t(`ADMIN.INGREDIENTS.${translationKey}`))
                    .join(', ')}.`}
                />
              )}
            </>
          }
          selectedItems={
            !isEmpty(supplierProductAllergens)
              ? supplierProductAllergens
              : ingredientAllergens.map((allergen) => ({
                  ...allergen,
                  name: i18next.t(`ADMIN.INGREDIENTS.${allergen.translationKey}`),
                }))
          }
          width={INPUT_WIDTH.FULL}
          onSelectionChange={onChange}
        />
      </IngredientAllergenDropdown>
    );
  }
}

export class IsStrategicAndEdibleInput extends React.PureComponent {
  render() {
    const {
      isStrategicId,
      isEdibleId,
      isStrategicChecked,
      isEdibleChecked,
      handleIsStrategicClick,
      handleIsEdibleClick,
      isReadOnly,
    } = this.props;

    return (
      <ToggleContainer>
        <InlineContainer>
          <ToggleSwitch
            checked={isStrategicChecked}
            disabled={isReadOnly}
            handleClick={handleIsStrategicClick}
            id={isStrategicId}
          />
          <Text>{`${i18next.t('ADMIN.INGREDIENTS.STRATEGIC_TOGGLE')}`}</Text>
          <Tooltip text={`${i18next.t('ADMIN.INGREDIENTS.STRATEGIC_TOGGLE_TOOLTIP')}`} />
        </InlineContainer>

        <InlineContainer>
          <ToggleSwitch
            checked={isEdibleChecked}
            disabled={isReadOnly}
            handleClick={handleIsEdibleClick}
            id={isEdibleId}
          />
          <Text>{`${i18next.t('ADMIN.INGREDIENTS.IS_EDIBLE_TOGGLE')}`}</Text>
          <Tooltip text={`${i18next.t('ADMIN.INGREDIENTS.IS_EDIBLE_TOGGLE_TOOLTIP')}`} />
        </InlineContainer>
      </ToggleContainer>
    );
  }
}

/*************************************/
/* Inputs Ingredient Form Configuration */
/*************************************/

export const IngredientNameInput = {
  keyProperty: 'name',
  component: Input,
  props: ({ ingredient, onIngredientChange, isReadOnly }) => ({
    label: `* ${i18next.t('ADMIN.INGREDIENTS.LABEL_NAME')}`,
    type: 'text',
    readOnly: isReadOnly,
    onChange: (event) => handleOnChangeIngredient(ingredient, onIngredientChange, event, 'name'),
  }),
};

export const IngredientCategoryInput = {
  keyProperty: 'categoryId',
  component: Dropdown,
  props: ({
    ingredient,
    onIngredientChange,
    ingredientsCategories,
    isReadOnly,
    isChildCreation,
    ...props
  }) => {
    const noneCategory = ingredientsCategories.find(({ name }) => !name);

    const selectedCategory =
      ingredientsCategories.find(({ id }) => id === ingredient.categoryId) || noneCategory;

    return {
      label: `${i18next.t('ADMIN.INGREDIENTS.LABEL_CATEGORY')} :`,
      items: ingredientsCategories,
      selectedItem: selectedCategory,
      onSelectionChange: (selectedItem, _, isUnselected) => {
        handleOnChangeIngredient(
          ingredient,
          onIngredientChange,
          { target: { value: isUnselected ? null : selectedItem.name } },
          'categoryId',
          !!selectedItem ? selectedItem.id : null,
        );
      },
      sortBy: (items) => sortCategoryAndSubCategoryDropdown(items),
      labelStyle: { font: theme.fonts.textSmall },
      width: INPUT_WIDTH.FULL,
      placeholder: i18next.t('GENERAL.CHOOSE_PLACEHOLDER'),
      isDisabled: isReadOnly,
      button: !isChildCreation
        ? {
            id: 1,
            text: i18next.t('GENERAL.LIST_CREATE_CATEGORY'),
            handleClick: () => {
              handleCategoryCreation('category', props);
            },
            iconSrc: '/images/inpulse/add-black-small.svg',
          }
        : null,
    };
  },
};

export const IngredientUnitSelector = (props) => {
  const { ingredient, onIngredientChange } = props;

  const selectedUnit = SMALL_UNITS_DROPDOWN_ITEMS().find((unit) => unit.id === ingredient.unit);

  return (
    <Dropdown
      customStyle={{ marginBottom: '28px' }} // not fitting the 8px rule to match legacy page style
      isDisabled={!!ingredient.id}
      isLabelTextSmall={true}
      isRequired={true}
      items={SMALL_UNITS_DROPDOWN_ITEMS()}
      label={`${i18next.t('GENERAL.UNIT')} :`}
      selectedItem={selectedUnit}
      width={INPUT_WIDTH.FULL}
      onSelectionChange={(selectedItem) => {
        handleOnChangeIngredient(ingredient, onIngredientChange, selectedItem.id, 'unit');
      }}
    />
  );
};

export const IngredientUnityInput = {
  keyProperty: 'unit',
  component: IngredientUnitSelector,
  props: ({ ingredient, onIngredientChange }) => ({
    ingredient,
    onIngredientChange,
  }),
};

export const IngredientCostInput = {
  keyProperty: 'cost',
  component: Input,
  props: ({
    ingredient,
    onIngredientChange,
    isReadOnly,
    isCostInputDisabled = false,
    currency,
  }) => {
    const validSupplierProducts = ingredient.supplierProducts.filter((supplierProduct) => {
      if (!+supplierProduct.price || !supplierProduct.isUsedForCost) {
        return false;
      }

      const ingredientUnitHasConversions = supplierProduct.conversions.some(
        ({ convertedUnit }) => convertedUnit === getIngredientUnit(ingredient.unit),
      );

      return isSameUnit(supplierProduct, ingredient.unit) || ingredientUnitHasConversions;
    });

    const tooltipTexts = getTooltipTexts();

    return {
      type: 'text',
      label: `${i18next.t('ADMIN.INGREDIENTS.LABEL_COST')} ${
        ingredient.unit ? `(${currency.alphabeticCode}/${getIngredientUnit(ingredient.unit)})` : ''
      } `,
      readOnly: !!validSupplierProducts.length || isReadOnly || isCostInputDisabled,
      tooltipText: validSupplierProducts.length
        ? tooltipTexts.INGREDIENT_COST_AUTOMATIC_CALCULATION_SUCCESS
        : tooltipTexts.INGREDIENT_COST_AUTOMATIC_CALCULATION_FAILURE,
      onChange: (event) => handleOnChangeIngredient(ingredient, onIngredientChange, event, 'cost'),
    };
  },
};

export const IngredientAllergensInput = {
  keyProperty: 'allergens',
  component: AllergensInput,
  props: ({ ingredient, onIngredientChange, availableAllergens, isReadOnly }) => ({
    availableAllergens,
    ingredientAllergens: ingredient.allergens,
    supplierProductAllergens: ingredient.supplierProductAllergens,
    isReadOnly,
    onChange: (event) =>
      handleOnChangeIngredient(ingredient, onIngredientChange, event, 'allergens'),
  }),
};

export const IngredientIsStrategicAndEdibleInput = {
  keyProperty: 'isStrategic',
  component: IsStrategicAndEdibleInput,
  props: ({ ingredient, onIngredientChange, isReadOnly }) => ({
    isStrategicId: 'is-strategic',
    isEdibleId: 'is-edible',
    isStrategicChecked: ingredient.isStrategic,
    isEdibleChecked: ingredient.isEdible,
    isReadOnly,
    handleIsStrategicClick: () =>
      handleOnChangeIngredient(
        ingredient,
        onIngredientChange,
        !ingredient.isStrategic,
        'isStrategic',
      ),
    handleIsEdibleClick: () =>
      handleOnChangeIngredient(ingredient, onIngredientChange, !ingredient.isEdible, 'isEdible'),
  }),
};

export const getIngredientUnit = (unit) => {
  if (unit === 'unit') {
    return 'unité';
  } else if (unit === 'g') {
    return 'kg';
  } else if (unit === 'mL') {
    return 'L';
  }
  return unit;
};

/*************************/
/* Input Change Handlers */
/*************************/

/**
 * Generic method that is in charge of updating the ingredient from the inputs that the user changes
 *
 * @param {Ingredient} ingredient           - The original ingredient when the user performs the change in an input
 * @param {Function} onIngredientChange     - Method used to set the local state of the ingredient variable
 * @param {Event} event                     - The event emitted by the input OnChange method
 * @param {String} fieldName                - The field name on which apply the change
 */
export function handleOnChangeIngredient(
  ingredient,
  onIngredientChange,
  event,
  fieldName,
  selectedItemId = null,
) {
  let updatedFieldValue =
    fieldName === 'unit' ||
    fieldName === 'allergens' ||
    fieldName === 'isStrategic' ||
    fieldName === 'isEdible'
      ? event
      : event.target.value;

  if (updatedFieldValue && fieldName === 'cost') {
    updatedFieldValue = formatTextNumberInputValue(updatedFieldValue);
  }

  const updatedIngredient = { ...ingredient, [fieldName]: updatedFieldValue };

  if (fieldName === 'categoryId' && !!selectedItemId) {
    updatedIngredient.category = event.target.value;
    updatedIngredient.categoryId = selectedItemId;
  }

  onIngredientChange({ ...updatedIngredient });
}

export const handleCategoryCreation = (propertyName, props) => {
  const { setItemDropdownPropertyName, setOpenModalAddNewItemDropdown } = props;
  setItemDropdownPropertyName(propertyName);

  setOpenModalAddNewItemDropdown(true);
};

export default {
  // Inputs
  IngredientNameInput,
  IngredientUnityInput,
  // Handlers methods
  handleOnChangeIngredient,
};
